import React, { FunctionComponent, useContext, useState } from "react";
import { useBoolean } from "@fluentui/react-hooks";
import { IPaymentComponent } from "./PaymentComponent.types";
import { useNavigate } from "react-router-dom";
import { IResponseMessage, QuotationInfoType } from "../../Quotation.types";
import { QuotationContext } from "../../QuotationContext";
import {
  InitialStateOfResponseMessage,
  PaymentErrorDefualtState,
} from "../../Common/QuotationData";
import { IQuotationAdyenPaymentPayload } from "../../Common/Interfaces/IQuotePayload";
import { QuotationService } from "../../../../services/Quotations/QuotationService";
import {
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  MessageBar,
  MessageBarType,
  PrimaryButton,
  Stack,
  TextField,
} from "@fluentui/react";
import { stackTokensWithGap20 } from "../../QuotationStyle";
import { urlPattern } from "../../../../Utils/Common";

export const PaymentComponent: FunctionComponent<IPaymentComponent> = (
  props: IPaymentComponent
) => {
  const { isEditPanelOpen, dismissEditPanel } = props;
  const navigate = useNavigate();
  const { quotationInfo, setQuotationInfo } = useContext(
    QuotationContext
  ) as QuotationInfoType;
  const [isEditable, { setTrue: enableEdit, setFalse: disableEdit }] =
    useBoolean(false);
  const [adyenPaymentLink, setAdyenPaymentLink] = useState(
    quotationInfo.adyenPaymentLink || ""
  );
  const [errorState, setErrorState] = useState(PaymentErrorDefualtState);
  const [quotationSuccessMessage, setQuotationSuccessMessage] =
    useState<IResponseMessage>(InitialStateOfResponseMessage);
  const [quotationFailureMessage, setQuotationFailureMessage] =
    useState<IResponseMessage>(InitialStateOfResponseMessage);
  const [isLoading, setIsLoading] = useState(false);
  const title =
    isEditable || !quotationInfo.adyenPaymentLink
      ? `Provide Payment Link`
      : `Current Payment Link`;
  const yesButtonLabel = quotationInfo.adyenPaymentLink
    ? isEditable
      ? "Save and Next"
      : "Edit"
    : "Submit";
  const validatePaymentFields = () => {
    let newErrorState = {
      ...errorState,
    };

    if (adyenPaymentLink === "") {
      newErrorState.adyenPaymentLink = true;
    } else if (errorState.adyenPaymentLink) {
      newErrorState.adyenPaymentLink = false;
    }
    if (!urlPattern.test(adyenPaymentLink)) {
      newErrorState.invalidPaymentLink = true;
    } else if (errorState.invalidPaymentLink) {
      newErrorState.invalidPaymentLink = false;
    }

    setErrorState(newErrorState);

    return !(
      newErrorState.adyenPaymentLink || newErrorState.invalidPaymentLink
    );
  };
  const onClickSaveAndNext = () => {
    setIsLoading(true);
    if (validatePaymentFields()) {
      const adyenPaymentPayload: IQuotationAdyenPaymentPayload = {
        quoteId: quotationInfo.quotationId,
        partitionKey: quotationInfo.partitionKey,
        adyenPaymentLink: adyenPaymentLink,
      };
      QuotationService.addAdyenPaymentLink(adyenPaymentPayload)
        .then((response) => {
          if (response.data.code === 200) {
            setQuotationSuccessMessage({
              showMessage: true,
              message: response.data.message,
            });
            setTimeout(() => {
              setQuotationSuccessMessage({
                showMessage: false,
                message: "",
              });
              disableEdit();
              dismissEditPanel();
              setIsLoading(false);
              setQuotationInfo({
                ...quotationInfo,
                ...response.data.data.data,
              });
              navigate("/DPRProforma", { state: quotationInfo });
            }, 3000);
          } else if (response.data.message) {
            setQuotationFailureMessage({
              showMessage: true,
              message: response.data.message,
            });
            setTimeout(() => {
              setQuotationFailureMessage({
                showMessage: false,
                message: "",
              });
              setIsLoading(false);
            }, 3000);
          }
        })
        .catch((error) => {
          setQuotationFailureMessage({
            showMessage: true,
            message: `Failed to add Adyen payment link for quotation ${quotationInfo.quotationNumber}`,
          });
          setTimeout(() => {
            setQuotationFailureMessage({
              showMessage: false,
              message: "",
            });
            setIsLoading(false);
          }, 3000);
        });
    } else {
      setQuotationFailureMessage({
        showMessage: true,
        message: "Please fill all the required fields with correct data",
      });
      setTimeout(() => {
        setQuotationFailureMessage({
          showMessage: false,
          message: "",
        });
        setIsLoading(false);
      }, 3000);
    }
  };

  const noButtonLabel = "Continue";
  return (
    <Dialog
      hidden={!isEditPanelOpen}
      dialogContentProps={{
        type: DialogType.normal,
        title: `${title}`,
        subText: ``,
      }}
      modalProps={{
        isBlocking: true,
      }}
      onDismiss={() => {
        setAdyenPaymentLink("");
        dismissEditPanel();
      }}
    >
      <Stack verticalFill verticalAlign="center" tokens={stackTokensWithGap20}>
        {quotationSuccessMessage?.showMessage && (
          <MessageBar
            messageBarType={MessageBarType.success}
            onDismiss={() =>
              setQuotationSuccessMessage &&
              setQuotationSuccessMessage(InitialStateOfResponseMessage)
            }
          >
            {quotationSuccessMessage.message}
          </MessageBar>
        )}
        {quotationFailureMessage?.showMessage && (
          <MessageBar
            messageBarType={MessageBarType.error}
            onDismiss={() =>
              setQuotationFailureMessage &&
              setQuotationFailureMessage(InitialStateOfResponseMessage)
            }
          >
            {quotationFailureMessage.message}
          </MessageBar>
        )}
        <TextField
          placeholder={adyenPaymentLink}
          value={adyenPaymentLink}
          readOnly={!!quotationInfo.adyenPaymentLink && !isEditable}
          disabled={
            (quotationInfo.adyenPaymentLink && !isEditable) || isLoading
          }
          onChange={(e, newValue) => {
            setAdyenPaymentLink && setAdyenPaymentLink(newValue || "");
          }}
          type="url"
          errorMessage={
            errorState && errorState.adyenPaymentLink
              ? "Payment link is required"
              : errorState && errorState.invalidPaymentLink
              ? "Payment link is invalid"
              : ""
          }
          onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
            validatePaymentFields();
          }}
          required
        />
      </Stack>
      <DialogFooter>
        <PrimaryButton
          text={yesButtonLabel}
          onClick={() => {
            if (isEditable || !quotationInfo.adyenPaymentLink) {
              onClickSaveAndNext();
            } else {
              enableEdit();
            }
          }}
          disabled={isLoading}
        />
        {quotationInfo.adyenPaymentLink && (
          <DefaultButton
            text={noButtonLabel}
            onClick={() => {
              setAdyenPaymentLink("");
              dismissEditPanel();
              navigate("/DPRProforma", { state: quotationInfo });
            }}
            disabled={isLoading}
          />
        )}
      </DialogFooter>
    </Dialog>
  );
};
